import { Notification } from '@model/notification';

/**
 * Sorts an array of notifications by their `dateTimeSent` field in descending order.
 *
 * The most recent notifications (newest) will appear first in the sorted array.
 * This function mutates the original array.
 *
 * @param notifications - Array of notifications to sort.
 * @returns The sorted array of notifications (same reference as input).
 */
export const sortNotificationsByDateTimeSent = (
  notifications: Notification[],
): Notification[] =>
  notifications.sort(
    (dateA, dateB) =>
      new Date(dateB.dateTimeSent).getTime() -
      new Date(dateA.dateTimeSent).getTime(),
  );

/**
 * Creates a local notification to inform the user that the maximum number of followees has been reached.
 *
 * The notification is assigned a fixed UUID (`max-followee-notification`) and is initialized
 * as unread and not deleted, with the current date/time as its sent timestamp.
 *
 * @param title - The title of the notification.
 * @param text - The body text of the notification.
 * @returns A {@link Notification} object representing the max followee warning.
 */
export const createMaxFolloweeNotification = (
  title: string,
  text: string,
): Notification => ({
  uuid: 'max-followee-notification',
  isRead: false,
  text,
  dateTimeSent: new Date().toISOString(),
  title,
  isDeleted: false,
});
